import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { authenticatedFetch } from '@/lib/api';
import { trackReportExcelDownloaded } from '@/lib/analytics';
import type { TimeGranularity, CompareSlot } from '@/types/reports';
import { getDateRangeFromPreset } from '@/types/reports';

export type ReportType = 'summary' | 'charts' | 'compare';

interface UseExcelExportOptions {
  groupIds: string[];
  modelCodes?: string[];
  industryTypes?: string[];
  /** Only these units (Reports → Unit Status click-to-focus); null = no unit filter */
  unitIds?: string[] | null;
  startDate?: Date | null;
  endDate?: Date | null;
  granularity?: TimeGranularity;
  compareSlots?: CompareSlot[];
  includeAllDeployments?: boolean;
}

interface UseExcelExportReturn {
  exportExcel: (reportType: ReportType) => Promise<void>;
  isExporting: boolean;
  error: Error | null;
}

/**
 * Hook to export report data as Excel file
 * Triggers download from backend API
 */
export function useExcelExport({
  groupIds,
  modelCodes = [],
  industryTypes = [],
  unitIds = null,
  startDate,
  endDate,
  granularity = 'weekly',
  compareSlots = [],
  includeAllDeployments = false,
}: UseExcelExportOptions): UseExcelExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const exportExcel = useCallback(async (reportType: ReportType) => {
    // __unassigned_* sentinels are sent as-is: the export route splits them
    // into p_unassigned_company_ids (PR #593). Stripping them here excluded
    // unassigned units from exports — and an unassigned-only selection sent
    // no groupIds at all, exporting every authorized group instead.
    const selectedGroupIds = groupIds.filter(Boolean);

    setIsExporting(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('reportType', reportType);

      if (selectedGroupIds.length > 0) {
        params.set('groupIds', selectedGroupIds.join(','));
      }
      if (modelCodes.length > 0) {
        params.set('modelCodes', modelCodes.join(','));
      }
      if (industryTypes.length > 0) {
        params.set('industryTypes', industryTypes.join(','));
      }
      if (unitIds && unitIds.length > 0) {
        params.set('unitIds', unitIds.join(','));
      }
      if (startDate) {
        params.set('startDate', startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        params.set('endDate', endDate.toISOString().split('T')[0]);
      }
      params.set('granularity', granularity);

      // Include all deployment periods for internal users
      if (includeAllDeployments) {
        params.set('includeAllDeployments', 'true');
      }

      // For compare mode, include slot data
      if (reportType === 'compare' && compareSlots.length > 0) {
        // Serialize compare slots for the API, resolving date ranges
        const serializedSlots = compareSlots.map(slot => {
          // Resolve the effective date range based on dateRangeMode
          let resolvedDateRange: { start: string | null; end: string | null } = { start: null, end: null };

          if (slot.dateRangeMode === 'linked') {
            // Use parent date range
            resolvedDateRange = {
              start: startDate?.toISOString().split('T')[0] || null,
              end: endDate?.toISOString().split('T')[0] || null,
            };
          } else if (slot.dateRangeMode === 'custom' && slot.customDateRange?.start && slot.customDateRange?.end) {
            // Use custom date range from slot
            resolvedDateRange = {
              start: slot.customDateRange.start.toISOString().split('T')[0],
              end: slot.customDateRange.end.toISOString().split('T')[0],
            };
          } else if (slot.dateRangeMode === 'preset' && slot.datePreset) {
            // Resolve date preset to actual dates
            const presetRange = getDateRangeFromPreset(slot.datePreset);
            resolvedDateRange = {
              start: presetRange.start?.toISOString().split('T')[0] || null,
              end: presetRange.end?.toISOString().split('T')[0] || null,
            };
          }

          return {
            label: slot.label,
            groupIds: slot.groupIds,
            modelCodes: slot.modelCodes,
            dateRange: resolvedDateRange,
            // Pass useGroupDates flag for per-group breakdown when multiple groups selected
            useGroupDates: slot.useFullGroup,
          };
        });
        params.set('compareSlots', JSON.stringify(serializedSlots));
      }

      const url = `/api/v1/export/excel?${params.toString()}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to export Excel report');
      }

      // Get the blob from response
      const blob = await response.blob();

      // Check if we got a valid blob
      if (blob.size === 0) {
        throw new Error('Received empty file from server');
      }

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `HT-${reportType}-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      trackReportExcelDownloaded(true);

    } catch (err) {
      trackReportExcelDownloaded(false);
      const errorMessage = err instanceof Error ? err.message : 'Export failed';

      // Show user-visible toast notification
      const displayMessage = errorMessage.includes('authenticated') || errorMessage.includes('timed out')
        ? 'Export failed: Session expired. Please refresh the page.'
        : `Export failed: ${errorMessage}`;
      toast.error(displayMessage);

      setError(err instanceof Error ? err : new Error('Export failed'));
      throw err;
    } finally {
      setIsExporting(false);
    }
  }, [groupIds, modelCodes, industryTypes, unitIds, startDate, endDate, granularity, compareSlots, includeAllDeployments]);

  return {
    exportExcel,
    isExporting,
    error,
  };
}
